import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import html2canvas from 'html2canvas';
import { jsPDF as JsPDF } from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RefreshIcon } from '../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as ExportIcon } from '../../assets/icons/export-icon.svg';
import { useAppBranding } from '../../config/branding';
import currentTimeZone from '../../config/timeZone';
import {
  Last12MonthProperties,
  Last24HoursProperties,
  Last7DaysProperties,
  LastMonthProperties,
} from '../../interface/dashboard.interface';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import dashboard from '../../utils/api/dashboard';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import CustomToggleButton from '../custom-toggle-button/CustomToggleButton';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  powerConsumed: number;
  totalDebitAmount: number;
}

interface FillMissing24hrsProperties {
  date: string;
  label: number;
  powerConsumed: number;
  totalDebitAmount: number;
}

/**
 * Function to handle missing data for the last 24 hours based on the current time zone
 * @param {FillMissing24hrsProperties[]} data - The input data
 * @returns {object[]} The result with complete data for the last 24 hours
 */
function fillMissingLast24HrsData(data: FillMissing24hrsProperties[]) {
  const today = new Date();

  // Generate the past 24 hours
  const past24Hours = [];
  for (let index = 0; index < 24; index += 1) {
    const date = new Date(today);
    date.setHours(today.getHours() - index, 0, 0, 0); // Adjust the hour and reset minutes, seconds, and ms
    const isoDate = new Intl.DateTimeFormat('en-CA', {
      timeZone: currentTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
    // Here date should be in this format 2025-02-20
    past24Hours.push({ date: isoDate, label: date.getHours() });
  }

  // Build the result array
  const result = past24Hours.map((eachHour) => {
    const dataForHour = data.find(
      (eachData) => eachData.date === eachHour.date && eachData.label === eachHour.label,
    );
    return {
      label: eachHour.label.toString(), // Hour (0-23)
      powerConsumed: dataForHour ? dataForHour.powerConsumed : 0, // Default to 0 if data not found
      totalDebitAmount: dataForHour ? dataForHour.totalDebitAmount : 0, // Default to 0 if data not found
    };
  });
  return result.reverse();
}

/**
 * Power consumed vs revenue chart
 */
function RevenueExpenseChart(properties: { stationId?: string }) {
  const { stationId } = properties;
  const branding = useAppBranding();
  const { t } = useTranslation('sharedComp');
  const printReference = useRef<HTMLDivElement | null>(null);
  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);
  const [exportButton, setExportButton] = useState<boolean>(true);
  const revenueLabel = t('revenue');
  const electricityLabel = t('electricity');
  const [timePeriods, setTimePeriods] = useState<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const chartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      {
        label: t('revenue'),
        data: powerConsumedData.map((d) => d.totalDebitAmount),
        borderColor: '#F79009',
        /**
         * Background
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const contentContext = chart.ctx;
          const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(247, 144, 9, 0.4)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin', // Fills to the previous dataset
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: t('electricity'),
        data: powerConsumedData.map((d) => d.powerConsumed),
        borderColor: '#2E9E3A',
        /**
         * Function to create a gradient bg color
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const chartContext = chart.ctx;
          const gradient = chartContext.createLinearGradient(0, 0, 1, chart.height);
          gradient.addColorStop(0, 'rgba(46, 158, 58, 1)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin',
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0, // Remove points on the line
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        /**
         * Function to crete tooltip bg color
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            // Check the dataset label and apply different colors
            if (context.tooltipItems[0].dataset.label === revenueLabel) {
              return 'rgba(247, 144, 9, 0.9)'; // Orange for Revenue
            }
            if (context.tooltipItems[0].dataset.label === electricityLabel) {
              return 'rgba(46, 158, 58, 0.9)'; // Green for Electricity
            }
          }
          return 'rgba(0, 0, 0, 0.8)'; // Default color
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate to title for chart
           * @param context
           * @returns
           */
          title: (context: any) => {
            // Set title based on dataset
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel === revenueLabel ? revenueLabel : electricityLabel;
          },
          /**
           * Function to show tooltip data
           * @param context
           * @returns
           */
          label: (context: any) => {
            // Format the label with currency
            const datasetLabel = context?.dataset?.label;
            return `${context?.raw.toLocaleString()}  ${datasetLabel === 'Revenue' ? branding.text.currency : 'Kwh'}`;
          },
        },
      },
    },

    interaction: {
      intersect: false, // Don't require an exact point to show the tooltip
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: false, // Hides the tick labels on the y-axis (revenue and expense values)
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
    },
  };

  const timePeriod: {
    label: string;
    value: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months';
  }[] = [
    {
      label: t('24hours'),
      value: 'last24hours',
    },
    {
      label: t('7days'),
      value: 'last7days',
    },
    {
      label: t('lastMonth'),
      value: 'lastMonth',
    },

    {
      label: t('last12months'),
      value: 'last12months',
    },
  ];

  /**
   * Function to fill missing months in chart
   * @param data
   * @returns
   */
  function fillMissingMonths(data: ChartDataProperties[]) {
    const today = new Date();
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // Generate past 12 months dynamically
    const past12Months = [];
    for (let index = 0; index < 12; index += 1) {
      const date = new Date(today.getFullYear(), today.getMonth() - index, 1); // Get the first day of the month
      past12Months.push({
        label: months[date.getMonth()], // Month name
        year: date.getFullYear(), // Year
      });
    }

    // Map input data for easy lookup
    const dataMap = new Map(data.map((item) => [item.label, item.powerConsumed]));
    const revenueMap = new Map(data.map((item) => [item.label, item.totalDebitAmount]));

    // Generate result array with missing months filled
    const result = past12Months.map(({ label, year }) => {
      const key = label; // Use the month name as key
      return {
        label: `${label} ${year}`, // Combine month and year for output
        powerConsumed: dataMap.get(key) || 0, // Default to 0 if month is missing
        totalDebitAmount: revenueMap.get(key) || 0, // Default 0
      };
    });

    return result.reverse();
  }

  /**
   * Function to generate the map data for past 30 days
   * @param data
   * @returns
   */
  function fillMissingLastMonth(data: ChartDataProperties[]) {
    const today = new Date();
    const past30Days = [];

    // Generate the past 30 days as ISO date strings
    for (let index = 0; index < 30; index += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - index);
      past30Days.push(date);
    }

    // Map backend data into a dictionary for easy lookup
    const dataMap = new Map(data.map((item) => [item.label.split('T')[0], item.powerConsumed]));
    const revenueMap = new Map(
      data.map((item) => [item.label.split('T')[0], item.totalDebitAmount]),
    );

    // Date formatter to get "Nov 18" format
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    });

    // Build the result array
    const result = past30Days.map((date) => {
      const isoDate = date.toISOString().split('T')[0]; // Use the ISO date for lookup
      return {
        label: dateFormatter.format(date), // Format label as "Nov 18"
        powerConsumed: dataMap.get(isoDate) || 0,
        totalDebitAmount: revenueMap.get(isoDate) || 0, // Default 0
      };
    });

    return result.reverse();
  }

  /**
   * Function to add missing data in last 7 days
   * @param data
   * @returns
   */
  function fillMissingLast7Days(data: ChartDataProperties[]) {
    const today = new Date();
    const last7Days = [];

    // Generate the last 7 days as ISO date strings
    for (let index = 0; index < 7; index += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - index);
      last7Days.push(date);
    }

    // Map backend data into a dictionary for easy lookup
    const dataMap = new Map(data.map((item) => [item.label.split('T')[0], item.powerConsumed]));
    const revenueMap = new Map(
      data.map((item) => [item.label.split('T')[0], item.totalDebitAmount]),
    );

    // Date formatter to get "Nov 14" format
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    });

    // Build the result array
    const result = last7Days.map((date) => {
      const isoDate = date.toISOString().split('T')[0]; // Use ISO date for lookup
      return {
        label: dateFormatter.format(date), // Format label as "Nov 14"
        powerConsumed: dataMap.get(isoDate) || 0, // Default to 0 if date not found
        totalDebitAmount: revenueMap.get(isoDate) || 0, // Default 0
      };
    });

    return result.reverse();
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    setIsLoading(true);

    try {
      const response = await dashboard.getPowerConsumedReport({
        timePeriod: range,
        timeZone: currentTimeZone,
        stationId,
      });
      if (range === 'last12months' && response.status === 200) {
        const updatedMapData: ChartDataProperties[] = response.data.map(
          (eachMonth: Last12MonthProperties) => ({
            label: eachMonth.monthName,
            powerConsumed: eachMonth.totalPowerConsumed,
            totalDebitAmount: eachMonth.totalDebitAmount,
          }),
        );
        setPowerConsumedData(fillMissingMonths(updatedMapData));
      } else if (range === 'lastMonth' && response.status === 200) {
        const updatedMapData: ChartDataProperties[] = response.data.map(
          (eachMonth: LastMonthProperties) => ({
            label: eachMonth.date,
            powerConsumed: eachMonth.totalPowerConsumed,
            totalDebitAmount: eachMonth.totalDebitAmount,
          }),
        );
        setPowerConsumedData(fillMissingLastMonth(updatedMapData));
      } else if (range === 'last7days' && response.status === 200) {
        const updatedMapData: ChartDataProperties[] = response.data.map(
          (eachMonth: Last7DaysProperties) => ({
            label: eachMonth.date,
            powerConsumed: eachMonth.totalPowerConsumed,
            totalDebitAmount: eachMonth.totalDebitAmount,
          }),
        );
        setPowerConsumedData(fillMissingLast7Days(updatedMapData));
      } else if (range === 'last24hours' && response.status === 200) {
        const updatedMapData: {
          date: string;
          label: number;
          powerConsumed: number;
          totalDebitAmount: number;
        }[] = response.data.map((eachMonth: Last24HoursProperties) => ({
          label: eachMonth.hour,
          date: eachMonth.date,
          powerConsumed: eachMonth.totalPowerConsumed,
          totalDebitAmount: eachMonth.totalDebitAmount,
        }));

        setPowerConsumedData(fillMissingLast24HrsData(updatedMapData));
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * Export the PDF file
   */
  const generatePdf = () => {
    setExportButton(false);
    if (printReference.current) {
      const example = printReference.current;
      setTimeout(async () => {
        const pdf = new JsPDF('l', 'mm', 'a4'); // A4 size: 210mm x 297mm
        const canvas = await html2canvas(example, { scale: 2 });
        const imgData = canvas.toDataURL('image/png');
        setExportButton(true);

        const imgWidth = 270; // Width of A4 in mm
        const pageHeight = 257; // Height of A4 in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;

        let position = 15;

        // Add the first page
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Add additional pages if content overflows
        while (heightLeft > 0) {
          position = heightLeft - imgHeight; // Adjust the position
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save('graph.pdf');
      }, 1000);
    }
  };

  /**
   * Refreshing chart data
   */
  const handleRefresh = () => {
    fetchPowerConsumedReport(timePeriods);
  };

  useEffect(() => {
    if (stationId) {
      fetchPowerConsumedReport(timePeriods);
    }
  }, [stationId, timePeriods]);

  return (
    <div className="revenue-expense-chart-wrap" ref={printReference}>
      <div className="revenue-expense-chart-header-wrap">
        {exportButton && <h1>{t('powerVsRevenue')}</h1>}
        <div className="revenue-expense-chart-header-container">
          <div className="chart-toggle-button">
            {exportButton && (
              <CustomToggleButton
                labels={timePeriod.map((duration) => t(duration.label))}
                onToggle={(index, label) =>
                  setTimePeriods(
                    timePeriod.find((duration) => duration.label === label)?.value || 'last24hours',
                  )
                }
                defaultActiveIndex={0}
              />
            )}
          </div>
          <div className="chart-label-wrap">
            <MyButton
              buttonType="none"
              hoverLabel="Refresh"
              label=""
              svgIcon={<RefreshIcon />}
              onClickFunc={handleRefresh}
            />
            <div className="each-chart-label">
              <div className="each-label-color" />
              <p>{t('revenue')}</p>
            </div>
            <div className="each-chart-label">
              <div className="each-label-color" />
              <p>{t('electricity')}</p>
            </div>
          </div>
          <div className="chart-export-button">
            {exportButton && (
              <MyButton
                label={t('exportData')}
                svgIcon={<ExportIcon />}
                buttonType="back"
                disableHoverEffect
                onClickFunc={generatePdf}
              />
            )}
          </div>
        </div>
      </div>
      <div className="revenue-expense-chart-container">
        {isLoading ? (
          <div className="bar-chart-loader">
            <BatteryLoader />
          </div>
        ) : (
          <Line
            data={chartData}
            options={options}
            height={200} // Explicit height for the chart
            width={900}
          />
        )}
      </div>
    </div>
  );
}

export default RevenueExpenseChart;
